// AYA Fiscal Center — profil fiscal, stats du jour, simulateur, documents, journal (sandbox).
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ShieldCheck, FlaskConical, FileText, RotateCcw, QrCode, Play } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const uid = () => { try { return localStorage.getItem("jsonfirst_civ_userid"); } catch { return null; } };
const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR")} FCFA`;

const SIM_DEFAULT = { activityType: "COMMERCE", customerType: "B2C", transactionType: "SALE", paymentMethod: "MOBILE_MONEY", totalAmount: 10000, currency: "XOF", country: "CI" };
const STATUS_COLORS = { FISCAL_CERTIFIED: "#34D399", FISCAL_REQUIRES_REVIEW: "#F5A623", FISCAL_FAILED: "#F87171", FISCAL_EXEMPT: "#60A5FA" };

export default function FiscalCenterPage() {
  const navigate = useNavigate();
  const biz = uid();
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [docs, setDocs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [registry, setRegistry] = useState(null);
  const [sim, setSim] = useState(SIM_DEFAULT);
  const [simRes, setSimRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exportMonth, setExportMonth] = useState(new Date().toISOString().slice(0, 7));

  const load = useCallback(() => {
    if (!biz) return;
    axios.get(`${API}/api/v3/fiscal/stats`, { params: { ayaBusinessId: biz } }).then((r) => { if (r.data?.ok) { setStats(r.data.stats); setProfile(r.data.profile); } }).catch(() => {});
    axios.get(`${API}/api/v3/fiscal/documents`, { params: { ayaBusinessId: biz } }).then((r) => setDocs(r.data?.documents || [])).catch(() => {});
    axios.get(`${API}/api/v3/fiscal/audit`, { params: { ayaBusinessId: biz } }).then((r) => setLogs(r.data?.logs || [])).catch(() => {});
  }, [biz]);
  useEffect(() => {
    if (!biz) navigate("/login?next=/pro/fiscal");
  }, [biz, navigate]);
  useEffect(() => { load(); axios.get(`${API}/api/v3/fiscal/registry`).then((r) => setRegistry(r.data)).catch(() => {}); }, [load]);

  const toggleVat = async () => {
    const next = profile?.vatStatus === "ASSUJETTI_TVA" ? "NON_ASSUJETTI_TVA" : "ASSUJETTI_TVA";
    try {
      await axios.post(`${API}/api/v3/fiscal/profiles`, { ayaBusinessId: biz, legalName: profile?.legalName || "Mon Commerce AYA", vatStatus: next, activities: ["COMMERCE", "SERVICES"], taxRegime: profile?.taxRegime || "SANDBOX" });
      toast.success(`Profil mis à jour : ${next.replace(/_/g, " ")} ✓`);
      load();
    } catch { toast.error("Mise à jour impossible"); }
  };

  const activateProfile = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/v3/fiscal/profiles`, {
        ayaBusinessId: biz, country: "CI", legalName: "Mon Commerce AYA", tradeName: "Mon Commerce",
        taxRegime: "SANDBOX", vatStatus: "NON_ASSUJETTI_TVA", activities: ["COMMERCE", "SERVICES"],
        fneStatus: "SANDBOX", rneStatus: "SANDBOX",
      });
      if (r.data?.ok) { toast.success("Profil fiscal sandbox activé ✓"); load(); }
    } catch { toast.error("Activation impossible"); }
    setBusy(false);
  };
  const runSim = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/v3/fiscal/simulate`, { ...sim, totalAmount: Number(sim.totalAmount), ayaBusinessId: biz, transactionId: `SIM-${Date.now()}` });
      setSimRes(r.data);
    } catch { toast.error("Simulation impossible"); }
    setBusy(false);
  };
  const emitTest = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/v3/fiscal/transactions`, { ...sim, totalAmount: Number(sim.totalAmount), ayaBusinessId: biz, transactionId: `TX-${Date.now()}`, sourceModule: "BUSINESS_DESK", customerId: biz });
      if (r.data?.document) toast.success(`${r.data.document.type} ${r.data.document.number} émis (sandbox) ✓`);
      else toast(`Statut : ${r.data?.fiscalStatus || "?"}`);
      load();
    } catch { toast.error("Émission impossible"); }
    setBusy(false);
  };
  const creditNote = async (d) => {
    try {
      const r = await axios.post(`${API}/api/v3/fiscal/documents/${d.documentId}/credit-note`, { reason: "Correction demandée par le commerçant" });
      if (r.data?.ok) { toast.success(`Avoir ${r.data.creditNote.number} créé ✓`); load(); }
      else toast.error(r.data?.error || "Avoir impossible");
    } catch (e) { toast.error(e.response?.data?.error || "Avoir impossible"); }
  };
  const Sel = ({ k, options }) => (
    <select value={sim[k]} onChange={(e) => setSim((s) => ({ ...s, [k]: e.target.value }))} style={S.sel} data-testid={`fiscal-sim-${k}`}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  return (
    <div style={S.page} data-testid="fiscal-center-page">
      <header style={S.header}>
        <button style={S.hbtn} data-testid="fiscal-back" onClick={() => navigate("/pro")}><ChevronLeft size={20} /></button>
        <h1 style={S.htitle}>AYA Fiscal Center <span style={S.sandbox}>SANDBOX</span></h1>
        <span style={{ width: 34 }} />
      </header>
      <div style={S.container}>
        {/* Profil fiscal */}
        <div style={S.card} data-testid="fiscal-profile-card">
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <ShieldCheck size={18} color="#D4AF37" />
            <b style={{ flex: 1, fontSize: 13.5 }}>Profil fiscal</b>
            {profile ? <span style={S.tagOk}>{profile.fiscalProfileStatus}</span> : <span style={S.tagWarn}>ABSENT</span>}
          </div>
          {profile ? (
            <div style={{ fontSize: 10.5, color: "#9DB4A6", marginTop: 7, lineHeight: 1.6 }}>
              {profile.legalName} · Régime : {profile.taxRegime} · TVA : {profile.vatStatus}
              <button onClick={toggleVat} data-testid="fiscal-vat-toggle" style={{ marginLeft: 6, background: "none", border: "none", color: "#34D399", fontSize: 10, fontWeight: 800, cursor: "pointer", textDecoration: "underline" }}>Basculer</button><br />
              FNE : <b style={{ color: "#34D399" }}>{profile.fneStatus}</b> · RNE : <b style={{ color: "#34D399" }}>{profile.rneStatus}</b> · API : <b style={{ color: "#F5A623" }}>SANDBOX</b>
            </div>
          ) : (
            <button style={S.btn} data-testid="fiscal-activate-profile" disabled={busy} onClick={activateProfile}>Activer mon profil fiscal (sandbox)</button>
          )}
        </div>

        {/* Stats du jour */}
        <div style={S.statGrid} data-testid="fiscal-stats">
          {[["FNE émises", stats?.fne, "#34D399"], ["RNE émis", stats?.rne, "#60A5FA"], ["Avoirs", stats?.avoirs, "#F5A623"], ["Erreurs", stats?.errors, "#F87171"], ["En attente", stats?.pendingReview, "#A78BFA"]].map(([l, v, c]) => (
            <div key={l} style={S.stat}><div style={{ fontSize: 19, fontWeight: 900, color: c }}>{v ?? 0}</div><div style={{ fontSize: 8.6, color: "#9DB4A6", fontWeight: 700 }}>{l}</div></div>
          ))}
        </div>

        {/* Simulateur */}
        <div style={S.card} data-testid="fiscal-simulator">
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 9 }}>
            <FlaskConical size={17} color="#34D399" /><b style={{ fontSize: 13.5 }}>Fiscal Simulator</b>
            <span style={{ fontSize: 8.5, color: "#9DB4A6", fontWeight: 700 }}>N'appelle jamais la production DGI</span>
          </div>
          <div style={S.simGrid}>
            <Sel k="activityType" options={registry?.activities || ["COMMERCE"]} />
            <Sel k="customerType" options={registry?.customerTypes || ["B2C", "B2B", "B2G"]} />
            <Sel k="paymentMethod" options={registry?.paymentMethods || ["MOBILE_MONEY", "CASH"]} />
            <input style={S.sel} type="number" value={sim.totalAmount} data-testid="fiscal-sim-amount"
              onChange={(e) => setSim((s) => ({ ...s, totalAmount: e.target.value }))} placeholder="Montant" />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 9 }}>
            <button style={{ ...S.btn, flex: 1, marginTop: 0 }} data-testid="fiscal-sim-run" disabled={busy} onClick={runSim}><Play size={13} /> Simuler</button>
            <button style={{ ...S.btn, flex: 1, marginTop: 0, background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.5)", color: "#D4AF37" }} data-testid="fiscal-emit-test" disabled={busy} onClick={emitTest}>Émettre (sandbox)</button>
          </div>
          {simRes && (
            <div style={S.simOut} data-testid="fiscal-sim-result">
              <div>Décision : <b style={{ color: STATUS_COLORS[`FISCAL_${simRes.decision?.status}`] || "#34D399" }}>{simRes.decision?.documentType}</b> · {simRes.decision?.reasonCode}</div>
              {simRes.decision?.ruleId && <div>Règle : {simRes.decision.ruleId} (v{simRes.decision.ruleVersion})</div>}
              {simRes.decision?.tax && <div>HT {fmt(simRes.decision.tax.subtotal)} · TVA {fmt(simRes.decision.tax.taxAmount)} · TTC {fmt(simRes.decision.tax.totalAmount)}</div>}
              {simRes.decision?.missingParams && <div style={{ color: "#F5A623" }}>Paramètres manquants : {simRes.decision.missingParams.join(", ")}</div>}
              {simRes.profileAnalyzed && <div>Profil analysé : TVA {simRes.profileAnalyzed.vatStatus} · FNE {simRes.profileAnalyzed.fneStatus}</div>}
            </div>
          )}
        </div>

        {/* Documents */}
        <div style={S.card} data-testid="fiscal-documents">
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 7 }}><FileText size={16} color="#60A5FA" /><b style={{ fontSize: 13.5, flex: 1 }}>Documents</b></div>
          {/* Export CSV mensuel (déclarations DGI) */}
          <div style={{ display: "flex", gap: 7, alignItems: "center", marginBottom: 9 }} data-testid="fiscal-export-row">
            <input
              type="month"
              data-testid="fiscal-export-month"
              value={exportMonth}
              onChange={(e) => setExportMonth(e.target.value)}
              style={{ flex: 1, padding: "7px 9px", borderRadius: 9, fontSize: 11.5, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.16)", color: "#ECF5EE", colorScheme: "dark" }}
            />
            <button
              data-testid="fiscal-export-csv"
              onClick={() => {
                if (!exportMonth) { toast.error("Choisis un mois"); return; }
                window.open(`${API}/api/v3/fiscal/export.csv?ayaBusinessId=${encodeURIComponent(biz)}&month=${exportMonth}`, "_blank");
                toast.success("Export CSV lancé ✓");
              }}
              style={{ padding: "8px 12px", borderRadius: 9, background: "linear-gradient(135deg,#10B981,#0E7A4F)", color: "#fff", border: "none", fontWeight: 800, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}
            >⬇ Export CSV (DGI)</button>
          </div>
          {docs.length === 0 && <div style={{ fontSize: 11, color: "#9DB4A6" }} data-testid="fiscal-docs-empty">Aucun document fiscal pour l'instant.</div>}
          {docs.slice(0, 8).map((d) => (
            <div key={d.documentId} style={S.docRow} data-testid={`fiscal-doc-${d.documentId}`}>
              <span style={{ ...S.docType, background: d.type === "FNE" ? "#0E7A4F" : d.type === "RNE" ? "#1D4ED8" : "#B45309" }}>{d.type === "CREDIT_NOTE" ? "AVOIR" : d.type}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 10.5, fontWeight: 800 }}>{d.number}</span>
                <span style={{ fontSize: 9, color: "#9DB4A6" }}>{fmt(d.totalAmount)} · {String(d.createdAt).slice(0, 16).replace("T", " ")}</span>
              </span>
              {d.qrData && <QrCode size={15} color="#9DB4A6" />}
              <button
                style={{ ...S.avoirBtn, background: "rgba(16,185,129,0.14)", border: "1px solid rgba(16,185,129,0.45)", color: "#34D399" }}
                data-testid={`fiscal-pdf-${d.documentId}`}
                onClick={() => window.open(`${API}/api/v3/fiscal/documents/${d.documentId}/pdf`, "_blank")}
              ><FileText size={11} /> PDF</button>
              {d.type !== "CREDIT_NOTE" && (
                <button style={S.avoirBtn} data-testid={`fiscal-avoir-${d.documentId}`} onClick={() => creditNote(d)}><RotateCcw size={11} /> Avoir</button>
              )}
            </div>
          ))}
        </div>

        {/* Journal fiscal */}
        <div style={S.card} data-testid="fiscal-audit">
          <b style={{ fontSize: 13.5 }}>Journal fiscal (append-only)</b>
          {logs.slice(0, 6).map((l, i) => (
            <div key={i} style={{ fontSize: 9.5, color: "#9DB4A6", padding: "6px 0", borderBottom: "1px solid rgba(16,185,129,0.12)", fontFamily: "monospace" }}>
              {String(l.timestamp).slice(5, 19).replace("T", " ")} · {l.module} · {l.documentType || "—"} · <span style={{ color: STATUS_COLORS[l.status] || "#CFE5D6" }}>{l.status}</span>{l.ruleId ? ` · ${l.ruleId}` : ""}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { minHeight: "100vh", background: "radial-gradient(ellipse at top, #08170F 0%, #050D08 65%, #030805 100%)", color: "#ECF5EE", paddingBottom: 30, overflowX: "hidden" },
  container: { maxWidth: 560, margin: "0 auto", padding: "0 14px", minWidth: 0 },
  header: { display: "flex", alignItems: "center", gap: 8, padding: "10px 10px 8px", maxWidth: 560, margin: "0 auto", position: "sticky", top: 0, zIndex: 40, background: "rgba(5,13,8,0.95)", backdropFilter: "blur(10px)" },
  hbtn: { width: 34, height: 34, borderRadius: 11, border: "none", background: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  htitle: { flex: 1, textAlign: "center", margin: 0, fontSize: 15.5, fontWeight: 800, color: "#fff" },
  sandbox: { fontSize: 8, fontWeight: 900, color: "#F5A623", border: "1px solid rgba(245,166,35,0.5)", borderRadius: 999, padding: "2px 7px", verticalAlign: "2px" },
  card: { background: "rgba(10,36,24,0.7)", border: "1px solid rgba(16,185,129,0.3)", borderRadius: 16, padding: 13, marginTop: 10 },
  tagOk: { fontSize: 8.5, fontWeight: 800, color: "#34D399", border: "1px solid rgba(52,211,153,0.5)", borderRadius: 999, padding: "3px 8px" },
  tagWarn: { fontSize: 8.5, fontWeight: 800, color: "#F5A623", border: "1px solid rgba(245,166,35,0.5)", borderRadius: 999, padding: "3px 8px" },
  btn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", height: 38, marginTop: 10, borderRadius: 12, border: "none", background: "linear-gradient(135deg,#10B981,#0E7A4F)", color: "#fff", fontSize: 11.5, fontWeight: 800, cursor: "pointer" },
  statGrid: { display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 7, marginTop: 10 },
  stat: { background: "rgba(10,36,24,0.7)", border: "1px solid rgba(16,185,129,0.28)", borderRadius: 13, padding: "10px 3px", textAlign: "center" },
  simGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 7 },
  sel: { width: "100%", background: "#071A10", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 10, color: "#ECF5EE", fontSize: 11, fontWeight: 700, padding: "10px 8px", outline: "none" },
  simOut: { marginTop: 10, padding: 10, borderRadius: 12, background: "#071A10", border: "1px solid rgba(16,185,129,0.25)", fontSize: 10.5, lineHeight: 1.7, color: "#CFE5D6" },
  docRow: { display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid rgba(16,185,129,0.12)" },
  docType: { fontSize: 8.5, fontWeight: 900, color: "#fff", borderRadius: 7, padding: "4px 7px", flexShrink: 0 },
  avoirBtn: { display: "inline-flex", alignItems: "center", gap: 4, height: 26, padding: "0 9px", borderRadius: 999, border: "1px solid rgba(245,166,35,0.5)", background: "rgba(245,166,35,0.08)", color: "#F5A623", fontSize: 9, fontWeight: 800, cursor: "pointer", flexShrink: 0 },
};
